import './WBAgentIcons.css';

import {
  CalloutIcon,
  type CalloutIconProps,
  Icon,
  type IconProps,
} from '@wandb/components';
import React from 'react';

/*
 * WORKAROUND for a gap in @wandb/components.
 *
 * `Icon` and `CalloutIcon` take `color` as a fixed union of palette names
 * (`teal`, `moon`, `sienna`, ...). There is no cobalt entry and no way to pass
 * a CSS variable, so `color="teal"` stays teal inside the Forge shell while
 * the rest of the shell renders the Axiom cobalt accent. The app's semantic
 * tokens (`--wb-accent-icon` and `--wb-accent-icon-bg` in
 * `src/css/theme-tokens.css`, which copy the shades the library paints for
 * `color="teal"` outside Forge) already resolve to teal standalone and cobalt
 * under `html[data-forge-shell="true"]`.
 *
 * `Icon` accepts `color="currentcolor"`, so `WBAgentIcon` wraps the glyph in a
 * span carrying the token. `CalloutIcon` strips `style` and `className`, so
 * `WBAgentCalloutIcon` overrides its color custom properties from
 * `WBAgentIcons.css` instead.
 *
 * Remove this file once @wandb/components exposes a theme-aware accent tone
 * on `Icon` and `CalloutIcon` (an `accent` value in `AutoColor`, or a `color`
 * that accepts the `--wb-accent*` tokens). At that point replace
 * `WBAgentIcon` with `Icon` and `WBAgentCalloutIcon` with `CalloutIcon`, and
 * change `WBAgentIconColor` back to `IconProps['color']`.
 */

/**
 * WBAgentIconColor is `Icon`'s `color` plus `accent`, which follows the app's
 * semantic accent token (teal in the standalone app, cobalt inside the Forge
 * shell).
 */
export type WBAgentIconColor = IconProps['color'] | 'accent';

export type WBAgentIconProps = Omit<IconProps, 'color'> & {
  color?: WBAgentIconColor;
};

/** WBAgentIcon is `Icon` with the extra `accent` color. */
export const WBAgentIcon = ({color, ...props}: WBAgentIconProps) =>
  color === 'accent' ? (
    <span className="inline-flex text-wb-accent-icon">
      <Icon {...props} color="currentcolor" />
    </span>
  ) : (
    <Icon {...props} color={color} />
  );

/** WBAgentCalloutIcon is `CalloutIcon` with the extra `accent` color. */
export const WBAgentCalloutIcon = ({
  color,
  ...props
}: Omit<CalloutIconProps, 'color'> & {
  color?: CalloutIconProps['color'] | 'accent';
}) =>
  color === 'accent' ? (
    <span className="wbagent-accent-callout contents">
      <CalloutIcon {...props} />
    </span>
  ) : (
    <CalloutIcon {...props} color={color} />
  );
